import * as THREE from 'three';

export interface ActorMeshOptions {
  kind: 'self' | 'remotePlayer' | 'mob';
  entityType?: string;
  color: string;
  accessoriesJson?: string;
  pose?: 'idle' | 'jumping';
}

export interface ActorMeshBundle {
  group: THREE.Group;
  pickMesh: THREE.Mesh;
  materials: THREE.Material[];
  labelOffset: number;
}

interface Accessories {
  head?: string;
  held?: string;
}

export function createActorMesh(options: ActorMeshOptions): ActorMeshBundle {
  const group = new THREE.Group();
  const materials: THREE.Material[] = [];
  const renderAsMob = shouldRenderAsMob(options);

  if (renderAsMob) {
    buildMob(group, materials, options);
  } else {
    buildPlayer(group, materials, options);
  }

  const pickMesh = new THREE.Mesh(
    getCachedGeometry('SphereGeometry', 0.42, 10, 10),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false }),
  );
  pickMesh.position.y = 0.48;
  group.add(pickMesh);

  return {
    group,
    pickMesh,
    materials,
    labelOffset: renderAsMob ? 1.18 : 1.28,
  };
}

function shouldRenderAsMob(options: ActorMeshOptions): boolean {
  if (options.kind === 'mob') {
    return true;
  }
  const entityType = (options.entityType ?? 'player').toLowerCase();
  return entityType !== 'player';
}

function buildPlayer(
  group: THREE.Group,
  materials: THREE.Material[],
  options: ActorMeshOptions,
): void {
  const bodyMaterial = makeMaterial(options.color, materials, {
    emissive: options.kind === 'self' ? '#1d4ed8' : undefined,
    emissiveIntensity: options.kind === 'self' ? 0.22 : 0,
  });
  const coreMaterial = makeMaterial('#f8fafc', materials, { emissive: '#ffffff', emissiveIntensity: 0.16 });
  const body = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.28, 18, 18), bodyMaterial);
  body.position.y = 0.42;
  group.add(body);

  const core = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.14, 14, 14), coreMaterial);
  core.position.y = 0.44;
  group.add(core);

  const eyeMaterial = makeMaterial('#1a1a1a', materials, { emissive: '#1a1a1a', emissiveIntensity: 0.05 });
  const leftEye = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.04, 8, 8), eyeMaterial);
  const rightEye = leftEye.clone();
  leftEye.position.set(-0.08, 0.46, 0.2);
  rightEye.position.set(0.08, 0.46, 0.2);
  group.add(leftEye);
  group.add(rightEye);

  const accessories = parseAccessories(options.accessoriesJson);
  addHeadAccessory(group, materials, accessories.head);
  addHeldAccessory(group, materials, accessories.held);
}

function buildMob(
  group: THREE.Group,
  materials: THREE.Material[],
  options: ActorMeshOptions,
): void {
  const entityType = (options.entityType ?? 'mob').toLowerCase();

  if (entityType.includes('skeleton')) {
    buildSkeleton(group, materials, options);
    return;
  }

  if (entityType === 'ranger') {
    buildRangerBlob(group, materials, options);
    return;
  }

  const bodyMaterial = makeMaterial(options.color, materials, { emissive: options.color, emissiveIntensity: 0.18 });
  const accentMaterial = makeMaterial('#f8fafc', materials, { emissive: '#ffffff', emissiveIntensity: 0.24 });

  if (entityType.includes('ring') || entityType.includes('halo') || entityType.includes('gyre')) {
    const ring = new THREE.Mesh(getCachedGeometry('TorusGeometry', 0.3, 0.09, 10, 24), bodyMaterial);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.46;
    group.add(ring);
    const core = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.12, 12, 12), accentMaterial);
    core.position.y = 0.46;
    group.add(core);
    return;
  }

  if (entityType.includes('sun') || entityType.includes('floret')) {
    const core = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.21, 14, 14), bodyMaterial);
    core.position.y = 0.46;
    group.add(core);
    for (let index = 0; index < 8; index += 1) {
      const petal = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.06, 0.3, 5), accentMaterial);
      const angle = (Math.PI * 2 * index) / 8;
      petal.position.set(Math.cos(angle) * 0.3, 0.46, Math.sin(angle) * 0.3);
      petal.lookAt(0, 0.46, 0);
      petal.rotateX(Math.PI / 2);
      group.add(petal);
    }
    return;
  }

  if (
    entityType.includes('shard')
    || entityType.includes('sigil')
    || entityType.includes('vent')
    || entityType.includes('cluster')
  ) {
    const shard = new THREE.Mesh(getCachedGeometry('OctahedronGeometry', 0.28, 0), bodyMaterial);
    shard.position.y = 0.5;
    group.add(shard);
    const small = new THREE.Mesh(getCachedGeometry('OctahedronGeometry', 0.13, 0), accentMaterial);
    small.position.set(0.18, 0.72, -0.06);
    group.add(small);
    return;
  }

  if (entityType.includes('wisp') || entityType.includes('knot') || entityType.includes('comet')) {
    const head = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.2, 14, 14), bodyMaterial);
    head.position.y = 0.54;
    group.add(head);
    const tail = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.12, 0.5, 8), accentMaterial);
    tail.position.set(0, 0.22, -0.16);
    tail.rotation.x = Math.PI;
    group.add(tail);
    return;
  }

  const orb = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.26, 16, 16), bodyMaterial);
  orb.position.y = 0.46;
  group.add(orb);

  const orbitMaterial = makeMaterial('#ffffff', materials, { emissive: '#ffffff', emissiveIntensity: 0.5 });
  for (let index = 0; index < 3; index += 1) {
    const spark = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.05, 8, 8), orbitMaterial);
    const angle = (Math.PI * 2 * index) / 3 + Math.PI / 6;
    spark.position.set(Math.cos(angle) * 0.22, 0.52, Math.sin(angle) * 0.22);
    group.add(spark);
  }
}

function buildRangerBlob(
  group: THREE.Group,
  materials: THREE.Material[],
  options: ActorMeshOptions,
): void {
  const hoodOuterMaterial = makeMaterial('#2f6f3e', materials, {
    emissive: '#1f4d2a',
    emissiveIntensity: 0.14,
  });
  const hoodInnerMaterial = makeMaterial('#18311f', materials, {
    roughness: 0.8,
    metalness: 0.03,
  });
  const faceMaterial = makeMaterial(options.color || '#6cc04a', materials, {
    roughness: 0.88,
    metalness: 0.02,
  });
  const trimMaterial = makeMaterial('#88c057', materials, {
    emissive: '#4d7c0f',
    emissiveIntensity: 0.12,
  });
  const eyeMaterial = makeMaterial('#1a1a1a', materials, {
    emissive: '#1a1a1a',
    emissiveIntensity: 0.05,
    roughness: 0.9,
  });
  const baseGlowMaterial = makeMaterial(options.color || '#6cc04a', materials, {
    emissive: options.color || '#6cc04a',
    emissiveIntensity: 0.22,
    roughness: 0.68,
    metalness: 0.05,
  });

  const body = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.31, 24, 20), hoodOuterMaterial);
  body.position.set(0, 0.34, 0);
  body.scale.set(1.05, 0.86, 1.02);
  group.add(body);

  const hoodBack = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.33, 24, 20), hoodOuterMaterial);
  hoodBack.position.set(0, 0.54, -0.03);
  hoodBack.scale.set(1, 1.08, 0.94);
  group.add(hoodBack);

  const hoodPeak = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.22, 0.42, 18), hoodOuterMaterial);
  hoodPeak.position.set(0, 0.82, -0.02);
  hoodPeak.rotation.x = -0.16;
  group.add(hoodPeak);

  const hoodInner = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.24, 18, 16), hoodInnerMaterial);
  hoodInner.position.set(0, 0.57, 0.14);
  hoodInner.scale.set(1, 0.78, 0.72);
  group.add(hoodInner);

  const face = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.19, 20, 16), faceMaterial);
  face.position.set(0, 0.58, 0.25);
  face.scale.set(1.22, 0.56, 0.38);
  group.add(face);

  const leftEye = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.032, 10, 10), eyeMaterial);
  const rightEye = leftEye.clone();
  leftEye.position.set(-0.085, 0.585, 0.33);
  rightEye.position.set(0.085, 0.585, 0.33);
  group.add(leftEye);
  group.add(rightEye);

  const leftCheek = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.18, 16, 14), hoodOuterMaterial);
  const rightCheek = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.18, 16, 14), hoodOuterMaterial);
  leftCheek.position.set(-0.18, 0.53, 0.08);
  rightCheek.position.set(0.18, 0.53, 0.08);
  leftCheek.scale.set(0.7, 1.1, 0.82);
  rightCheek.scale.copy(leftCheek.scale);
  group.add(leftCheek);
  group.add(rightCheek);

  const leftLap = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.19, 16, 14), hoodOuterMaterial);
  const rightLap = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.19, 16, 14), hoodOuterMaterial);
  leftLap.position.set(-0.12, 0.31, 0.16);
  rightLap.position.set(0.12, 0.31, 0.16);
  leftLap.scale.set(0.72, 1.08, 0.55);
  rightLap.scale.copy(leftLap.scale);
  leftLap.rotation.z = 0.5;
  rightLap.rotation.z = -0.5;
  group.add(leftLap);
  group.add(rightLap);

  const trim = new THREE.Mesh(getCachedGeometry('TorusGeometry', 0.23, 0.038, 10, 24, Math.PI), trimMaterial);
  trim.position.set(0, 0.14, 0.15);
  trim.rotation.z = Math.PI;
  trim.rotation.x = -0.24;
  group.add(trim);

  const knotCenter = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.045, 12, 12), trimMaterial);
  knotCenter.position.set(0, 0.135, 0.21);
  group.add(knotCenter);

  for (const side of [-1, 1] as const) {
    const knotLoop = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.052, 12, 12), trimMaterial);
    knotLoop.position.set(side * 0.055, 0.135, 0.21);
    knotLoop.scale.set(0.95, 0.7, 0.45);
    group.add(knotLoop);

    const ribbon = new THREE.Mesh(getCachedGeometry('CapsuleGeometry', 0.022, 0.16, 4, 10), trimMaterial);
    ribbon.position.set(side * 0.045, 0.04, 0.2);
    ribbon.rotation.z = side * 0.2;
    ribbon.rotation.x = 0.08;
    group.add(ribbon);
  }

  const baseGlow = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.27, 0.31, 0.08, 24), baseGlowMaterial);
  baseGlow.position.set(0, 0.05, 0);
  group.add(baseGlow);
}

function buildSkeleton(
  group: THREE.Group,
  materials: THREE.Material[],
  options: ActorMeshOptions,
): void {
  const bodyColor = '#ffffff';
  const boneMaterial = new THREE.MeshToonMaterial({ color: bodyColor });
  materials.push(boneMaterial);

  const shadowMaterial = new THREE.MeshToonMaterial({ color: '#1a1a1a' });
  materials.push(shadowMaterial);

  const outlineMaterial = new THREE.MeshBasicMaterial({ color: '#000000', side: THREE.BackSide });
  materials.push(outlineMaterial);

  // Skull & Jaw
  const skull = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.20, 16, 16), boneMaterial);
  skull.scale.set(0.9, 1.05, 0.95);
  skull.position.set(0, 0.72, 0);
  group.add(skull);
  const skullOutline = new THREE.Mesh(skull.geometry, outlineMaterial);
  skullOutline.scale.setScalar(1.06);
  skull.add(skullOutline);

  const jaw = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.20, 0.12, 0.22), boneMaterial);
  jaw.position.set(0, 0.58, 0.04);
  group.add(jaw);
  const jawOutline = new THREE.Mesh(jaw.geometry, outlineMaterial);
  jawOutline.scale.setScalar(1.08);
  jaw.add(jawOutline);

  const leftEye = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.045, 8, 8), shadowMaterial);
  leftEye.position.set(-0.06, 0.73, 0.18);
  group.add(leftEye);

  const rightEye = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.045, 8, 8), shadowMaterial);
  rightEye.position.set(0.06, 0.73, 0.18);
  group.add(rightEye);

  const nose = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.03, 0.06, 4), shadowMaterial);
  nose.position.set(0, 0.66, 0.20);
  nose.rotation.x = -0.4;
  group.add(nose);

  // Neck
  const neck = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.03, 0.04, 0.12, 8), boneMaterial);
  neck.position.set(0, 0.52, 0);
  group.add(neck);

  // Spine
  const spine = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.04, 0.04, 0.32, 8), boneMaterial);
  spine.position.set(0, 0.36, -0.02);
  spine.rotation.x = 0.05;
  group.add(spine);
  const spineOutline = new THREE.Mesh(spine.geometry, outlineMaterial);
  spineOutline.scale.set(1.3, 1.05, 1.3);
  spine.add(spineOutline);

  // Clavicles (Shoulder bones)
  const clavicle = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.36, 0.04, 0.06), boneMaterial);
  clavicle.position.set(0, 0.50, 0);
  group.add(clavicle);
  const clavicleOutline = new THREE.Mesh(clavicle.geometry, outlineMaterial);
  clavicleOutline.scale.set(1.05, 1.3, 1.3);
  clavicle.add(clavicleOutline);

  // Ribs
  for (let i = 0; i < 4; i++) {
    const rib = new THREE.Mesh(getCachedGeometry('TorusGeometry', 0.14 - i * 0.015, 0.025, 8, 16), boneMaterial);
    rib.scale.set(1.15, 1, 0.85);
    rib.position.set(0, 0.45 - i * 0.06, 0.01);
    rib.rotation.x = -Math.PI / 2 + 0.1;
    group.add(rib);

    const ribOutline = new THREE.Mesh(rib.geometry, outlineMaterial);
    ribOutline.scale.setScalar(1.15);
    rib.add(ribOutline);
  }

  // Pelvis
  const pelvis = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.28, 0.11, 0.16), boneMaterial);
  pelvis.position.set(0, 0.16, -0.01);
  group.add(pelvis);
  const pelvisOutline = new THREE.Mesh(pelvis.geometry, outlineMaterial);
  pelvisOutline.scale.set(1.05, 1.15, 1.15);
  pelvis.add(pelvisOutline);

  const armOffsets = [-1, 1];
  for (const side of armOffsets) {
    const isJumping = options.pose === 'jumping';

    // Arms
    const upperArm = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.045, 0.025, 0.18, 8), boneMaterial);
    upperArm.position.set(side * 0.21, 0.45, 0);
    upperArm.rotation.z = side * (isJumping ? 0.6 : 0.2);
    if (isJumping) upperArm.rotation.x = -0.4;
    group.add(upperArm);
    const upperArmOutline = new THREE.Mesh(upperArm.geometry, outlineMaterial);
    upperArmOutline.scale.set(1.15, 1.05, 1.15);
    upperArm.add(upperArmOutline);

    const elbow = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.035, 8, 8), boneMaterial);
    elbow.position.set(side * (isJumping ? 0.25 : 0.24), 0.36, isJumping ? 0.04 : 0);
    group.add(elbow);

    const lowerArm = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.035, 0.02, 0.16, 8), boneMaterial);
    lowerArm.position.set(side * (isJumping ? 0.28 : 0.27), isJumping ? 0.33 : 0.29, isJumping ? 0.11 : 0.02);
    lowerArm.rotation.z = side * (isJumping ? 0.3 : 0.1);
    lowerArm.rotation.x = isJumping ? -1.2 : -0.15;
    group.add(lowerArm);
    const lowerArmOutline = new THREE.Mesh(lowerArm.geometry, outlineMaterial);
    lowerArmOutline.scale.set(1.2, 1.06, 1.2);
    lowerArm.add(lowerArmOutline);

    const hand = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.06, 0.08, 0.06), boneMaterial);
    hand.position.set(side * (isJumping ? 0.30 : 0.28), isJumping ? 0.30 : 0.20, isJumping ? 0.19 : 0.04);
    group.add(hand);
    const handOutline = new THREE.Mesh(hand.geometry, outlineMaterial);
    handOutline.scale.setScalar(1.15);
    hand.add(handOutline);
  }

  for (const side of armOffsets) {
    const isJumping = options.pose === 'jumping';

    // Legs
    const thigh = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.05, 0.03, 0.22, 8), boneMaterial);
    thigh.position.set(side * 0.11, isJumping ? 0.1 : 0.04, 0);
    thigh.rotation.z = side * (isJumping ? 0.3 : 0.05);
    if (isJumping) thigh.rotation.x = -0.6;
    group.add(thigh);
    const thighOutline = new THREE.Mesh(thigh.geometry, outlineMaterial);
    thighOutline.scale.set(1.12, 1.05, 1.12);
    thigh.add(thighOutline);

    const knee = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.04, 8, 8), boneMaterial);
    knee.position.set(side * (isJumping ? 0.14 : 0.12), isJumping ? 0.01 : -0.07, isJumping ? 0.06 : 0);
    group.add(knee);

    const calf = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.035, 0.025, 0.20, 8), boneMaterial);
    calf.position.set(side * (isJumping ? 0.14 : 0.12), isJumping ? -0.08 : -0.16, isJumping ? 0.01 : 0);
    if (isJumping) calf.rotation.x = 0.5;
    group.add(calf);
    const calfOutline = new THREE.Mesh(calf.geometry, outlineMaterial);
    calfOutline.scale.set(1.2, 1.05, 1.2);
    calf.add(calfOutline);

    const foot = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.06, 0.04, 0.14), boneMaterial);
    foot.position.set(side * (isJumping ? 0.14 : 0.12), isJumping ? -0.17 : -0.26, isJumping ? -0.04 : 0.03);
    if (isJumping) foot.rotation.x = 0.5;
    group.add(foot);
    const footOutline = new THREE.Mesh(foot.geometry, outlineMaterial);
    footOutline.scale.setScalar(1.15);
    foot.add(footOutline);
  }

  // Adjust overall body positioning if jumping to give a vertical hop
  if (options.pose === 'jumping') {
    group.position.y += 0.3;
  }

  const accessories = parseAccessories(options.accessoriesJson);
  addHeadAccessory(group, materials, accessories.head);
  addHeldAccessory(group, materials, accessories.held);
}

function addHeadAccessory(
  group: THREE.Group,
  materials: THREE.Material[],
  accessory?: string,
): void {
  if (!accessory) {
    return;
  }

  if (accessory === 'hood') {
    const hood = new THREE.Mesh(
      getCachedGeometry('SphereGeometry', 0.22, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2),
      makeMaterial('#115e59', materials),
    );
    hood.position.set(0, 0.62, 0);
    group.add(hood);
    return;
  }

  if (accessory === 'wizard_hat') {
    const hat = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.18, 0.36, 12), makeMaterial('#4338ca', materials));
    hat.position.set(0, 0.78, 0);
    group.add(hat);
    const brim = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.24, 0.24, 0.03, 16), makeMaterial('#312e81', materials));
    brim.position.set(0, 0.58, 0);
    group.add(brim);
    return;
  }

  if (accessory === 'helm') {
    const helm = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.22, 12, 12), makeMaterial('#64748b', materials));
    helm.position.set(0, 0.62, 0);
    group.add(helm);
    return;
  }

  if (accessory === 'horns') {
    for (const side of [-1, 1] as const) {
      const horn = new THREE.Mesh(getCachedGeometry('ConeGeometry', 0.05, 0.24, 6), makeMaterial('#92400e', materials));
      horn.position.set(side * 0.16, 0.76, -0.02);
      horn.rotation.z = side * 0.5;
      group.add(horn);
    }
  }
}

function addHeldAccessory(
  group: THREE.Group,
  materials: THREE.Material[],
  accessory?: string,
): void {
  if (!accessory) {
    return;
  }

  if (accessory === 'bow') {
    const bow = new THREE.Mesh(getCachedGeometry('TorusGeometry', 0.14, 0.02, 8, 18, Math.PI), makeMaterial('#86efac', materials));
    bow.position.set(0.24, 0.42, 0);
    bow.rotation.z = Math.PI / 2;
    group.add(bow);
    return;
  }

  if (accessory === 'staff') {
    const shaft = new THREE.Mesh(getCachedGeometry('CylinderGeometry', 0.02, 0.02, 0.42, 8), makeMaterial('#e2e8f0', materials));
    shaft.position.set(0.24, 0.42, 0);
    shaft.rotation.z = 0.15;
    group.add(shaft);
    const orb = new THREE.Mesh(getCachedGeometry('SphereGeometry', 0.05, 10, 10), makeMaterial('#c4b5fd', materials, { emissive: '#c4b5fd', emissiveIntensity: 0.45 }));
    orb.position.set(0.27, 0.64, 0);
    group.add(orb);
    return;
  }

  if (accessory === 'shield') {
    const shield = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.16, 0.22, 0.06), makeMaterial('#94a3b8', materials));
    shield.position.set(0.23, 0.4, 0);
    group.add(shield);
    return;
  }

  if (accessory === 'sword') {
    const blade = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.04, 0.34, 0.04), makeMaterial('#e2e8f0', materials));
    blade.position.set(0.24, 0.48, 0);
    blade.rotation.z = -0.35;
    group.add(blade);
    const hilt = new THREE.Mesh(getCachedGeometry('BoxGeometry', 0.12, 0.03, 0.05), makeMaterial('#854d0e', materials));
    hilt.position.set(0.18, 0.34, 0);
    hilt.rotation.z = -0.35;
    group.add(hilt);
  }
}

function parseAccessories(accessoriesJson?: string): Accessories {
  if (!accessoriesJson) {
    return {};
  }
  try {
    const parsed = JSON.parse(accessoriesJson) as { head?: unknown; held?: unknown };
    return {
      head: typeof parsed.head === 'string' ? parsed.head : undefined,
      held: typeof parsed.held === 'string' ? parsed.held : undefined,
    };
  } catch {
    return {};
  }
}

function makeMaterial(
  color: string,
  materials: THREE.Material[],
  overrides?: Partial<THREE.MeshStandardMaterialParameters>,
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: 0.55,
    metalness: 0.15,
    transparent: true,
    ...overrides,
  });
  materials.push(material);
  return material;
}

const geometryCache = new Map<string, THREE.BufferGeometry>();

function getCachedGeometry<
  T extends
  | 'SphereGeometry'
  | 'TorusGeometry'
  | 'ConeGeometry'
  | 'OctahedronGeometry'
  | 'CapsuleGeometry'
  | 'CylinderGeometry'
  | 'BoxGeometry'
>(type: T, ...args: any[]): THREE.BufferGeometry {
  const key = `${type}-${args.join('-')}`;
  let geo = geometryCache.get(key);
  if (!geo) {
    geo = new (THREE[type] as any)(...args) as THREE.BufferGeometry;
    geometryCache.set(key, geo);
  }
  return geo!;
}
